import client, { mock, isRealApiEnabled } from "./client";
import notesMock from "./mocks/notes";

// In-memory working copy so create / update / remove persist for the life of
// the session (mirrors how the real endpoints will behave). Swap this module's
// bodies for `client.*` calls when the backend lands; consumers stay the same.
let store = notesMock.slice();

const byNewestFirst = (a, b) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

/**
 * @endpoint GET /api/v1/ibos/:iboId/notes?page=&pageSize=
 * Returns a page of notes for an IBO, newest first, plus the total count so
 * the caller can render a "View more" / pagination affordance.
 * @returns {Promise<{ items: Note[], total: number, page: number, pageSize: number }>}
 */
export function list({ iboId, page = 1, pageSize = 5 } = {}) {
  if (isRealApiEnabled())
    return client.get(`/v1/ibos/${encodeURIComponent(iboId)}/notes`, {
      query: { page, pageSize },
    });
  const all = store
    .filter((n) => String(n.iboId) === String(iboId))
    .sort(byNewestFirst);
  const start = (Math.max(1, page) - 1) * pageSize;
  return mock({
    items: all.slice(start, start + pageSize),
    total: all.length,
    page,
    pageSize,
  });
}

/**
 * @endpoint POST /api/v1/ibos/:iboId/notes
 * @returns {Promise<Note>}
 */
export function create({ iboId, orderId = null, author, text }) {
  if (isRealApiEnabled())
    return client.post(`/v1/ibos/${encodeURIComponent(iboId)}/notes`, {
      orderId,
      author,
      text,
    });
  const note = {
    id: `note_new_${Date.now()}`,
    iboId: String(iboId),
    orderId: orderId || null,
    author: author || "support.user@wwg.local",
    text: String(text || "").trim(),
    createdAt: new Date().toISOString(),
  };
  store = [note, ...store];
  return mock(note);
}

/**
 * @endpoint PUT /api/v1/notes/:id
 * @returns {Promise<Note>}
 */
export function update(id, { text }) {
  if (isRealApiEnabled())
    return client.put(`/v1/notes/${encodeURIComponent(id)}`, { text });
  let updated = null;
  store = store.map((n) => {
    if (n.id !== id) return n;
    updated = { ...n, text: String(text || "").trim() };
    return updated;
  });
  return mock(updated);
}

/**
 * @endpoint DELETE /api/v1/notes/:id
 * @returns {Promise<{ id: string }>}
 */
export function remove(id) {
  if (isRealApiEnabled())
    return client.del(`/v1/notes/${encodeURIComponent(id)}`);
  store = store.filter((n) => n.id !== id);
  return mock({ id });
}
